import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { listFirmUsers, useListAllUsers, type FirmUser } from '@taxassist/api-client';
import { useAuth } from '../../contexts/AuthContext';

interface UserDisplayProps {
  userId: string | null | undefined;
  variant?: 'body1' | 'body2' | 'caption';
  color?: 'textPrimary' | 'textSecondary' | 'primary';
  contextFirmId?: string; // Firm ID where this user assignment was made
  fallback?: string; // Text to display when userId is null/undefined (default: "N/A")
}

/**
 * UserDisplay component that shows a user's name by their UUID
 * 
 * For ops portal (admin/reviewer context), uses the ops/users endpoint
 * to lookup admin and reviewer user details. Falls back to firm users API
 * for firm-scoped users.
 */
export const UserDisplay: React.FC<UserDisplayProps> = ({
  userId,
  variant = 'body2',
  color = 'textSecondary',
  contextFirmId,
  fallback = 'N/A'
}) => {
  const { user } = useAuth();

  // Only fetch admin users if current user is admin/reviewer
  const shouldFetchAdminUsers = user && (user.role === 'admin' || user.role === 'reviewer');
  console.log('UserDisplay: user:', user);
  console.log('UserDisplay: shouldFetchAdminUsers:', shouldFetchAdminUsers);
  
  const {
    data: adminUsersData,
    isLoading: adminUsersLoading
  } = useListAllUsers(
    shouldFetchAdminUsers && userId ? { roles: ['admin', 'reviewer'], limit: 500 } : undefined
  );

  // Try to find user in admin users first
  const adminUser = useMemo(() => {
    console.log('UserDisplay: Looking for userId:', userId);
    console.log('UserDisplay: adminUsersData:', adminUsersData);
    console.log('UserDisplay: shouldFetchAdminUsers:', shouldFetchAdminUsers);
    
    if (!userId || !adminUsersData?.users) {
      console.log('UserDisplay: No userId or no admin users data');
      return null;
    }
    
    const foundUser = adminUsersData.users.find(u => u.userId === userId);
    console.log('UserDisplay: Found admin user:', foundUser);
    return foundUser;
  }, [userId, adminUsersData]);

  // If we found the user in admin users, return their name
  if (adminUser) {
    const name = `${adminUser.firstName} ${adminUser.lastName}`.trim();
    return (
      <Typography variant={variant} color={color}>
        {name}
      </Typography>
    );
  }

  // If we're still loading admin users, show loading state
  if (shouldFetchAdminUsers && adminUsersLoading) {
    console.log('UserDisplay: Still loading admin users');
    return (
      <Typography variant={variant} color="textSecondary">
        ...
      </Typography>
    );
  }

  // If no userId provided, show fallback text
  if (!userId) {
    console.log(`UserDisplay: No userId provided, showing ${fallback}`);
    return (
      <Typography variant={variant} color="textSecondary">
        {fallback}
      </Typography>
    );
  }

  // Fall back to firm users lookup if contextFirmId is provided
  if (contextFirmId) {
    return <FirmUserDisplay userId={userId} contextFirmId={contextFirmId} variant={variant} color={color} />;
  }

  // Final fallback - show truncated userId
  const truncatedId = userId.substring(0, 8) + '...';
  return (
    <Typography variant={variant} color={color}>
      {truncatedId}
    </Typography>
  );
};

/**
 * Component for looking up users within a specific firm
 */
const FirmUserDisplay: React.FC<{
  userId: string;
  contextFirmId: string;
  variant: 'body1' | 'body2' | 'caption';
  color: 'textPrimary' | 'textSecondary' | 'primary';
}> = ({ userId, contextFirmId, variant, color }) => {
  const [displayName, setDisplayName] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const loadFirmUser = async () => {
      setIsLoading(true);
      try {
        console.log(`UserDisplay: Looking up userId ${userId} in firm ${contextFirmId}`);
        
        const response = await listFirmUsers(contextFirmId, { limit: 100 });
        const user = response.users.find((u: FirmUser) => u.userId === userId);
        
        if (user) {
          const name = `${user.firstName} ${user.lastName}`.trim();
          console.log(`UserDisplay: Found firm user: ${name}`);
          setDisplayName(name);
        } else {
          console.log(`UserDisplay: User ${userId} not found in firm, using fallback`);
          setDisplayName(userId.substring(0, 8) + '...');
        }
      } catch (error) {
        console.warn(`UserDisplay: Error loading firm user ${userId}:`, error);
        setDisplayName(userId.substring(0, 8) + '...');
      } finally {
        setIsLoading(false);
      }
    };

    loadFirmUser();
  }, [userId, contextFirmId]);

  if (isLoading) {
    return (
      <Typography variant={variant} color="textSecondary">
        ...
      </Typography>
    );
  }

  return (
    <Typography variant={variant} color={color}>
      {displayName}
    </Typography>
  );
};